import type { ProductTileProps } from '@modules/products-category/util/product-tile-view-model.server';
import type { SearchResultItem } from '@modules/search';
import type { Variant } from '~/modules/products-category/type/index';
import getProductPriceDisplayProps from '~/modules/tile/util/get-product-tile-price-display-props';
import type { TileViewModel } from '../type/util';
import type { User } from '~/server/types';
import { getTileEditableItems } from '~/modules/directory/util/tile-helpers';
import { getProductPricing } from '@modules/product/util/get-product-pricing';
import type { TileCarouselImage } from '@modules/products-category/util/product-tile-view-model.server';

interface ProductTileViewModel extends TileViewModel<ProductTileProps> {
    productId: number;
    draft?: boolean;
    pending?: boolean;
    sliderImages: TileCarouselImage[];
}

export const mapESProductTileViewModel = (
    product: SearchResultItem,
    user: User,
    size: ProductTileProps['size'] = 'medium',
): ProductTileViewModel => {
    const displayPricing = user
        ? getProductPricing(product, user)
        : product.PricingDisplay;
    const { price, pricePrefix } = getProductPriceDisplayProps(displayPricing);
    const priceCurrency = displayPricing?.Currency || undefined;

    const variants: Variant[] =
        !product.ProductColours || product.ProductColours?.length <= 1 //consider a single variant to be no variants
            ? []
            : (product.ProductColours?.map((c): Variant => {
                  const uniqueKey = product.id + '-' + (c?.ID ?? c.DotDesktop);
                  return {
                      image: c.DotDesktop,
                      colorCode: c.Code,
                      size: 13,
                      key: uniqueKey,
                      description: c.Label,
                  };
              }) ?? []);

    return {
        productId: product.id,
        draft: product.Visibilities?.includes('Draft'),
        pending: product.Visibilities?.includes('PendingApproval'),
        tile: {
            id: product.id,
            image: getProductTileImage(product, false),
            seoDescription: product.SEODescription ?? '',
            professionalID: product.ProfessionalID ?? 0,
            professionalName: product.Professional ?? '',
            professionalLink: product.ProfessionalLink ?? '',
            productName: product.Title ?? '',
            productLink: product.Link ?? '',
            hasAwards: product.HasAwards ?? false,
            size,
            pricePrefix,
            price,
            priceCurrency,
            variants,
            ...getTileEditableItems({
                professional: {
                    ID: product.ProfessionalID || 0,
                    Link: product.ProfessionalLink || '',
                },
                itemType: 'products',
                itemLink: product.Link || '',
                user,
            }),
            canPurchase: !!product.CanPurchase,
            downloads: product.DownloadFileCategories?.sort(
                // we have legacy files in es that has no SortOrder which returns -1, these should be sorted last
                (a, b) =>
                    a.SortOrder === -1
                        ? 1
                        : b.SortOrder === -1
                          ? -1
                          : a.SortOrder - b.SortOrder,
            ),
        },
        sliderImages: getProductTileImage(product, true),
        link: product.Link ?? '',
    };
};

const getProductTileImage = (
    product: SearchResultItem,
    mapMultipleImages?: boolean,
): TileCarouselImage[] => {
    if (mapMultipleImages) {
        return (
            product.DirectoryTiles?.map((tile) => {
                return {
                    src: tile.Thumb || '',
                    mixBlendMode: tile.IsMultiplied ? 'multiply' : 'normal',
                    alt: product.Title || '',
                };
            }) || []
        );
    }

    return [
        {
            src: product.DirectoryTile?.Thumb ?? product.Thumb_lg ?? '',
            mixBlendMode: product.DirectoryTile?.IsMultiplied
                ? 'multiply'
                : 'normal',
            alt: product.Title || '',
        },
    ];
};
